// Utility functions for interacting with USDA CroplandCROS data

// DMV area bounding box coordinates (approximate)
// DC: 38.7951° N, 77.1185° W to 38.9955° N, 76.9094° W
// Maryland and Virginia surrounding areas
export const DMV_BOUNDS = {
  north: 39.5, // Northern Maryland
  south: 37.8, // Southern Virginia near DMV
  east: -76.0, // Eastern Maryland
  west: -78.0  // Western Virginia near DMV
};

// Function to fetch CroplandCROS data
// Note: This is a placeholder for the actual API integration
// The USDA CroplandCROS requires authentication and specific parameters
export const fetchCroplandData = async () => {
  try {
    // In a real implementation, this would be a call to the CroplandCROS API
    // For now, we'll simulate the API response with a placeholder
    console.log('Fetching CroplandCROS data for DMV area...');
    
    // Simulated API response delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Return a placeholder response
    // In a real implementation, we would process the GeoTIFF data here
    return {
      success: true,
      message: 'Data fetched successfully',
      // This would be the actual data from the API
      data: null
    };
  } catch (error) {
    console.error('Error fetching CroplandCROS data:', error);
    return {
      success: false,
      message: 'Failed to fetch data',
      error: error.message
    };
  }
};

// Function to download CroplandCROS GeoTIFF files for the DMV area
export const downloadCroplandGeoTIFF = async (year = 2023) => {
  try {
    console.log(`Downloading CroplandCROS GeoTIFF for DMV area (${year})...`);
    
    // In a real implementation, this would download the GeoTIFF file
    // For now, we'll simulate the download with a placeholder
    
    // Simulated download delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Return a placeholder response
    return {
      success: true,
      message: 'GeoTIFF downloaded successfully',
      // This would be the path to the downloaded file
      filePath: null
    };
  } catch (error) {
    console.error('Error downloading CroplandCROS GeoTIFF:', error);
    return {
      success: false,
      message: 'Failed to download GeoTIFF',
      error: error.message
    };
  }
};

// Convert CroplandCROS data to GeoJSON format for use with Leaflet
export const convertCroplandToGeoJSON = (croplandData) => {
  // In a real implementation, this would convert the CroplandCROS data to GeoJSON
  // For now, we'll return a placeholder GeoJSON object
  
  return {
    type: 'FeatureCollection',
    features: []
  };
};

// Get crop type information based on the CDL code
export const getCropTypeInfo = (cdlCode) => {
  // CDL codes and their corresponding crop types
  // Source: https://www.nass.usda.gov/Research_and_Science/Cropland/sarsfaqs2.php
  const cdlCodes = {
    1: { name: 'Corn', color: '#FFD700' },
    2: { name: 'Cotton', color: '#FFA07A' },
    3: { name: 'Rice', color: '#00FFFF' },
    4: { name: 'Sorghum', color: '#FF6347' },
    5: { name: 'Soybeans', color: '#32CD32' },
    6: { name: 'Sunflower', color: '#FFFF00' },
    10: { name: 'Peanuts', color: '#8B4513' },
    11: { name: 'Tobacco', color: '#A0522D' },
    12: { name: 'Sweet Corn', color: '#FFFF99' },
    13: { name: 'Pop or Orn Corn', color: '#FFEC8B' },
    14: { name: 'Mint', color: '#98FB98' },
    21: { name: 'Barley', color: '#EEE8AA' },
    22: { name: 'Durum Wheat', color: '#DAA520' },
    23: { name: 'Spring Wheat', color: '#F5DEB3' },
    24: { name: 'Winter Wheat', color: '#D2B48C' },
    25: { name: 'Other Small Grains', color: '#DEB887' },
    26: { name: 'Dbl Crop WinWht/Soybeans', color: '#BDB76B' },
    27: { name: 'Rye', color: '#8B7355' },
    28: { name: 'Oats', color: '#F4A460' },
    29: { name: 'Millet', color: '#CD853F' },
    36: { name: 'Alfalfa', color: '#7CFC00' },
    37: { name: 'Other Hay/Non Alfalfa', color: '#ADFF2F' },
    42: { name: 'Dry Beans', color: '#A52A2A' },
    43: { name: 'Potatoes', color: '#A0522D' },
    46: { name: 'Hops', color: '#006400' },
    47: { name: 'Spearmint', color: '#00FF7F' },
    48: { name: 'Peppermint', color: '#00FA9A' },
    49: { name: 'Buckwheat', color: '#8B4513' },
    50: { name: 'Fallow/Idle Cropland', color: '#DCDCDC' },
    53: { name: 'Peas', color: '#2E8B57' },
    54: { name: 'Tomatoes', color: '#FF6347' },
    55: { name: 'Caneberries', color: '#8B0000' },
    56: { name: 'Hops', color: '#006400' },
    57: { name: 'Herbs', color: '#3CB371' },
    58: { name: 'Clover/Wildflowers', color: '#FF1493' },
    59: { name: 'Sod/Grass Seed', color: '#7FFF00' },
    60: { name: 'Switchgrass', color: '#6B8E23' },
    61: { name: 'Fallow/Idle Cropland', color: '#DCDCDC' },
    66: { name: 'Cherries', color: '#8B0000' },
    67: { name: 'Peaches', color: '#FF69B4' },
    68: { name: 'Apples', color: '#FF0000' },
    69: { name: 'Grapes', color: '#800080' },
    70: { name: 'Christmas Trees', color: '#006400' },
    71: { name: 'Other Tree Crops', color: '#556B2F' },
    72: { name: 'Citrus', color: '#FFA500' },
    74: { name: 'Pecans', color: '#8B4513' },
    75: { name: 'Almonds', color: '#D2691E' },
    76: { name: 'Walnuts', color: '#8B4513' },
    77: { name: 'Pears', color: '#228B22' },
    204: { name: 'Pistachios', color: '#BDB76B' },
    205: { name: 'Triticale', color: '#CD853F' },
    206: { name: 'Carrots', color: '#FF8C00' },
    207: { name: 'Asparagus', color: '#7FFF00' },
    208: { name: 'Garlic', color: '#FFFACD' },
    209: { name: 'Cantaloupes', color: '#FFA07A' },
    214: { name: 'Broccoli', color: '#228B22' },
    216: { name: 'Peppers', color: '#FF4500' },
    219: { name: 'Greens', color: '#008000' },
    221: { name: 'Strawberries', color: '#FF0000' },
    222: { name: 'Squash', color: '#FFD700' },
    227: { name: 'Lettuce', color: '#90EE90' },
    229: { name: 'Pumpkins', color: '#FFA500' },
    242: { name: 'Blueberries', color: '#0000CD' },
    243: { name: 'Cabbage', color: '#008000' },
    244: { name: 'Cauliflower', color: '#FFFAF0' },
    245: { name: 'Celery', color: '#90EE90' },
    246: { name: 'Radishes', color: '#FF0000' },
    247: { name: 'Turnips', color: '#FFFFFF' },
    248: { name: 'Eggplants', color: '#800080' },
    249: { name: 'Gourds', color: '#F5DEB3' },
    250: { name: 'Cranberries', color: '#DC143C' },
    // NLCD-derived classes
    121: { name: 'Developed/Open Space', color: '#E8D1D1' },
    122: { name: 'Developed/Low Intensity', color: '#E4A696' },
    123: { name: 'Developed/Med Intensity', color: '#E29E8C' },
    124: { name: 'Developed/High Intensity', color: '#DDA67A' },
    131: { name: 'Barren', color: '#B2ADA3' },
    141: { name: 'Deciduous Forest', color: '#68AB5F' },
    142: { name: 'Evergreen Forest', color: '#1C6330' },
    143: { name: 'Mixed Forest', color: '#B5CA8D' },
    152: { name: 'Shrubland', color: '#CCBA7C' },
    176: { name: 'Grassland/Pasture', color: '#E2E2C1' },
    190: { name: 'Woody Wetlands', color: '#C4D6F1' },
    195: { name: 'Herbaceous Wetlands', color: '#A9CCD6' }
  };
  
  return cdlCodes[cdlCode] || { name: 'Unknown', color: '#CCCCCC' };
};

/**
 * Generate DMV area field boundaries based on common crops in the region
 * @returns {Object} GeoJSON FeatureCollection with field boundaries
 */
export const generateDMVFieldBoundaries = () => {
  // Common crops in the DMV area
  const dmvCrops = [
    { crop: 'Corn', code: 1 },
    { crop: 'Soybeans', code: 5 },
    { crop: 'Winter Wheat', code: 24 },
    { crop: 'Alfalfa', code: 36 },
    { crop: 'Other Hay/Non Alfalfa', code: 37 },
    { crop: 'Fallow/Idle Cropland', code: 50 },
    { crop: 'Apples', code: 68 },
    { crop: 'Grapes', code: 69 },
    { crop: 'Vegetables', code: 214 }, // Using Broccoli as a representative vegetable
    { crop: 'Deciduous Forest', code: 141 },
    { crop: 'Grassland/Pasture', code: 176 }
  ];
  
  // DMV area coordinates (centered around DC, Maryland, and Virginia)
  // These are approximate coordinates for demonstration purposes
  const dmvCoordinates = {
    // DC area
    dc: {
      center: [-77.0369, 38.9072],
      radius: 0.05
    },
    // Maryland areas (Montgomery, Prince George's, Frederick counties)
    maryland: {
      center: [-77.2, 39.1],
      radius: 0.2
    },
    // Virginia areas (Fairfax, Arlington, Loudoun counties)
    virginia: {
      center: [-77.4, 38.8],
      radius: 0.25
    }
  };
  
  // Generate field boundaries for the DMV area
  const features = [];
  let fieldId = 1;
  
  // Helper function to generate a polygon around a center point
  const generatePolygon = (center, size, irregularity = 0.2) => {
    // Validate inputs
    if (!Array.isArray(center) || center.length !== 2) {
      throw new Error('Center must be an array of [longitude, latitude]');
    }
    if (typeof size !== 'number' || size <= 0) {
      throw new Error('Size must be a positive number');
    }
    if (typeof irregularity !== 'number' || irregularity < 0 || irregularity > 1) {
      throw new Error('Irregularity must be a number between 0 and 1');
    }

    const points = [];
    const numPoints = 5 + Math.floor(Math.random() * 3); // 5-7 points for the polygon

    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * 2 * Math.PI;
      const radius = size * (1 - irregularity + Math.random() * irregularity * 2);

      const x = center[0] + radius * Math.cos(angle);
      const y = center[1] + radius * Math.sin(angle);

      points.push([x, y]);
    }

    // Close the polygon
    points.push([...points[0]]);

    return [points];
  };
  
  // Generate fields for each region
  Object.entries(dmvCoordinates).forEach(([region, location]) => {
    const numFields = 10 + Math.floor(Math.random() * 15); // 10-25 fields per region
    
    for (let i = 0; i < numFields; i++) {
      // Random position within the region
      const angle = Math.random() * 2 * Math.PI;
      const distance = Math.random() * location.radius;
      const x = location.center[0] + distance * Math.cos(angle);
      const y = location.center[1] + distance * Math.sin(angle);
      
      // Random field size (smaller for urban areas like DC)
      const fieldSize = region === 'dc' 
        ? 0.005 + Math.random() * 0.01 
        : 0.01 + Math.random() * 0.03;
      
      // Random crop from the DMV crops list
      const cropInfo = dmvCrops[Math.floor(Math.random() * dmvCrops.length)];
      const cropTypeInfo = getCropTypeInfo(cropInfo.code);
      
      // Random health status (weighted towards good)
      const healthStatus = Math.random() > 0.7 
        ? (Math.random() > 0.5 ? 'warning' : 'critical') 
        : 'good';
      
      // Random health score based on status
      let healthScore;
      if (healthStatus === 'good') {
        healthScore = 80 + Math.floor(Math.random() * 20);
      } else if (healthStatus === 'warning') {
        healthScore = 60 + Math.floor(Math.random() * 20);
      } else {
        healthScore = 30 + Math.floor(Math.random() * 30);
      }
      
      // Random planting date from the last year
      const plantMonth = 2 + Math.floor(Math.random() * 3); // March-May
      const plantDay = 1 + Math.floor(Math.random() * 28);
      const plantDate = `2023-${plantMonth.toString().padStart(2, '0')}-${plantDay.toString().padStart(2, '0')}`;
      
      // Calculate area in acres (approximate)
      const areaInAcres = Math.floor(fieldSize * 10000);
      
      // Create the feature
      features.push({
        type: 'Feature',
        properties: {
          id: fieldId++,
          name: `${region.toUpperCase()} Field ${i + 1}`,
          region: region.toUpperCase(),
          health: healthScore,
          status: healthStatus,
          area: areaInAcres,
          crop: cropInfo.crop,
          cropCode: cropInfo.code,
          color: cropTypeInfo.color,
          plantDate: plantDate
        },
        geometry: {
          type: 'Polygon',
          coordinates: generatePolygon([x, y], fieldSize)
        }
      });
    }
  });
  
  // Add some water features (ponds, reservoirs)
  const waterFeatures = [
    {
      name: 'Potomac River Section',
      type: 'water',
      capacity: '1,200,000 gallons',
      coordinates: [-77.1, 38.93]
    },
    {
      name: 'Chesapeake Bay Inlet',
      type: 'water',
      capacity: '3,500,000 gallons',
      coordinates: [-76.4, 39.1]
    },
    {
      name: 'Farm Reservoir',
      type: 'water',
      capacity: '450,000 gallons',
      coordinates: [-77.3, 39.0]
    },
    {
      name: 'Irrigation Pond',
      type: 'water',
      capacity: '180,000 gallons',
      coordinates: [-77.5, 38.85]
    }
  ];
  
  waterFeatures.forEach(water => {
    features.push({
      type: 'Feature',
      properties: {
        name: water.name,
        type: water.type,
        capacity: water.capacity
      },
      geometry: {
        type: 'Point',
        coordinates: water.coordinates
      }
    });
  });
  
  return {
    type: 'FeatureCollection',
    features: features
  };
};

/**
 * Fetches real data from the CroplandCROS API
 * @param {number} year - The year to fetch data for
 * @param {Object} bounds - The bounding box to fetch data for
 * @returns {Promise<Object>} - The response from the API
 */
export const fetchRealCroplandData = async (year = 2022) => {
  try {
    console.log(`[CroplandCROS] Fetching real data for year ${year} in DMV region`);

    // Validate year parameter
    const currentYear = new Date().getFullYear();
    if (year < 1997 || year > currentYear) {
      throw new Error(`Invalid year: ${year}. Must be between 1997 and ${currentYear}`);
    }

    // This is a real endpoint for the CroplandCROS API
    // For demo purposes, we're using a simplified approach
    const url = `https://nassgeodata.gmu.edu/CropScapeService/query_data?year=${year}&service=getcroplayer&format=json&bbox=${DMV_BOUNDS.west},${DMV_BOUNDS.south},${DMV_BOUNDS.east},${DMV_BOUNDS.north}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json'
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`CroplandCROS API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[CroplandCROS] Data fetched successfully:', data);
      return {
        success: true,
        data: data
      };
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        throw new Error('Request timeout: API took longer than 30 seconds to respond');
      }
      throw fetchError;
    }
  } catch (error) {
    console.error('[CroplandCROS] Error fetching data:', error);
    // Fall back to simulated data
    return {
      success: false,
      error: error.message,
      simulatedData: true
    };
  }
};

/**
 * Fetches crop statistics for the DMV area
 * @param {number} year - The year to fetch statistics for
 * @returns {Promise<Object>} - Crop statistics
 */
export const fetchCropStatistics = async (year = 2022) => {
  try {
    console.log(`[CroplandCROS] Fetching crop statistics for year ${year}`);

    // Validate year parameter
    const currentYear = new Date().getFullYear();
    if (year < 1997 || year > currentYear) {
      throw new Error(`Invalid year: ${year}. Must be between 1997 and ${currentYear}`);
    }

    // This is a real endpoint for the CroplandCROS API statistics
    const url = `https://nassgeodata.gmu.edu/CropScapeService/query_data?year=${year}&service=getcropstats&format=json&bbox=${DMV_BOUNDS.west},${DMV_BOUNDS.south},${DMV_BOUNDS.east},${DMV_BOUNDS.north}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json'
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`CroplandCROS API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[CroplandCROS] Statistics fetched successfully:', data);
      return {
        success: true,
        data: data
      };
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        throw new Error('Request timeout: API took longer than 30 seconds to respond');
      }
      throw fetchError;
    }
  } catch (error) {
    console.error('[CroplandCROS] Error fetching statistics:', error);
    // Return simulated statistics
    return {
      success: false,
      error: error.message,
      simulatedStats: {
        Corn: { acres: 125000, percentage: 28 },
        Soybeans: { acres: 110000, percentage: 25 },
        Wheat: { acres: 75000, percentage: 17 },
        Alfalfa: { acres: 45000, percentage: 10 },
        Cotton: { acres: 30000, percentage: 7 },
        Other: { acres: 58000, percentage: 13 }
      }
    };
  }
};

// Create a named export object for better ESLint compliance
const croplandCrosAPI = {
  fetchCroplandData,
  downloadCroplandGeoTIFF,
  convertCroplandToGeoJSON,
  getCropTypeInfo,
  generateDMVFieldBoundaries,
  DMV_BOUNDS,
  fetchRealCroplandData,
  fetchCropStatistics
};

export default croplandCrosAPI; 